import React from 'react'
import styled from 'styled-components'
import { Tag } from '../Tag'
import { Mono } from '../Mono'
import { CardRow } from '../CardRow'
import { StatusBadge } from '../StatusBadge'

export interface OrderRowStatus {
  label: string
  bg: string
  fg?: string
}

export interface OrderRowProps {
  id: string | number
  primary: string
  leading?: React.ReactNode
  tag?: string
  tagColor?: string
  date?: string
  status?: OrderRowStatus
  trailing?: React.ReactNode
  onClick?: () => void
  isLast?: boolean
  className?: string
}

export const OrderRow: React.FC<OrderRowProps> = ({
  id,
  primary,
  leading,
  tag,
  tagColor,
  date,
  status,
  trailing,
  onClick,
  isLast = false,
  className,
}) => {
  const interactive = typeof onClick === 'function'
  const handleKeyDown = interactive
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }
    : undefined

  return (
    <CardRow
      className={className}
      $columns='4.375rem minmax(0, 1fr) auto auto auto'
      $mobileColumns='auto minmax(0, 1fr) auto auto auto'
      $isLast={isLast}
      $interactive={interactive}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <CardRow.Cell>
        <CardRow.Stack>
          <Id>#{id}</Id>
          {tag && (
            <Tag color={tagColor} variant='tinted'>
              {tag}
            </Tag>
          )}
        </CardRow.Stack>
      </CardRow.Cell>

      <CardRow.Cell>
        <CardRow.Line>
          {leading}
          <CardRow.Stack>
            <PrimaryName>{primary}</PrimaryName>
          </CardRow.Stack>
        </CardRow.Line>
      </CardRow.Cell>

      <CardRow.Cell $align='center'>
        {date && <When>{date}</When>}
      </CardRow.Cell>

      <CardRow.Cell $align='center'>
        {status && (
          <StatusBadge label={status.label} color={status.bg} multiline />
        )}
      </CardRow.Cell>

      <CardRow.Cell $align='center'>{trailing}</CardRow.Cell>
    </CardRow>
  )
}

export default OrderRow

const Id = styled(Mono)`
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

const PrimaryName = styled.span`
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  font-weight: 500;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.fonts.sans};
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

const When = styled(Mono)`
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink2};
  white-space: nowrap;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.md};
  }
`
